import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/store';
import { logout } from '../api/auth';
import '../styles/Navbar.css';
import { NavbarHeightContext } from '../helpers/NavbarHeightContext'; // To get the height of the navbar
import UserProfile from './UserProfile';
import { getAvatarName, getCheckInStatus } from '../api/user';
import { ChatBotContext } from '../context/ChatBotContext';

const Navbar = ({ profileOption, setProfileOption }) => {
    const { setUser, setIsAuth, isAuth } = useAuthStore(store => store);
    const navigate = useNavigate();
    const location = useLocation();
    const [navPath, setNavPath] = useState(location.pathname);
    const { navbarRef } = useContext(NavbarHeightContext);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { setShow } = useContext(ChatBotContext);
    const uid = useAuthStore(store => store.user?.uid);
    const uidNotNull = !(uid === null || uid === undefined);
    const avatarNameQuery = useQuery({
        queryKey: ['avatarName', uid],
        queryFn: () => getAvatarName(uid),
        enabled: uidNotNull,
    });

    const checkInStatusQuery = useQuery({
        queryKey: ['checkin', uid],
        queryFn: () => getCheckInStatus(uid),
        enabled: uidNotNull,
    });

    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

    useEffect(() => {
        setNavPath(location.pathname);
    }, [location.pathname]);

    const handleLogout = async() => {
        await logout();
        setUser(null);
        setIsAuth(false);
        navigate('/');
        setNavPath('/');
        setShow(false);
    };

    useEffect(() => {
    // Handler to call on outside click of dropdown menu
        const handleOutsideClick = (event) => {
            if (isDropdownOpen && !event.target.closest('.nav-item.dropdown')) {
                setIsDropdownOpen(false);
            }
        };

        // Adding click event listener
        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            // Cleanup the event listener
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isDropdownOpen]);

    const menuItems = [
        { path: '/dashboard', text: 'Report' },
        { path: '/leaderboard', text: 'Leaderboard' },
        { path: '/avatar', text: 'Avatar' },
        { path: '/profile', text: 'Profile' },
    ];

    const authItems = [
        { path: '/', text: 'Home' },
        { path: '/login', text: 'Login' },
        { path: '/register', text: 'Register' },
    ];

    const handleLinkClick = (path) => {
        setNavPath(`/profile?tab=${path}`);
    };

    const handleProfileClick = (profileOption) => {
        setProfileOption(profileOption);
        setNavPath(`/profile?tab=${profileOption}`);
        toggleDropdown();
    };

    const navigationLink = (path, text, screenType) => {
        if (path === '/profile' && isAuth) {
            return (
                <li className="nav-item dropdown" key={path}>
                    <div
                        className={`item dropdown-toggle ${navPath === '/profile' ? 'bold-text' : ''}`}
                        id="navbarDropdown"
                        role="button"
                        aria-expanded={isDropdownOpen}
                        onClick={toggleDropdown}
                    >
                        {text}
                    </div>
                    <ul className={`dropdown-menu${isDropdownOpen ? ' show' : ''}`} aria-labelledby="navbarDropdown" {...(screenType === 'small-screen' ? { 'data-bs-dismiss': 'offcanvas' } : {})}>
                        <li>
                            <Link to={path} className={`dropdown-item ${profileOption === 'dailyreflection' ? 'active' : ''}`} onClick={() => handleProfileClick('dailyreflection')}>
                                <div className="profile-item">Daily Reflection</div>
                            </Link>
                        </li>
                        <li>
                            <Link to={path} className={`dropdown-item ${profileOption === 'emailreminder' ? 'active' : ''}`} onClick={() => handleProfileClick('emailreminder')}>
                                <div className="profile-item">Email Reminder</div>
                            </Link>
                        </li>
                        <li>
                            <Link to={path} className={`dropdown-item ${profileOption === 'settings' ? 'active' : ''}`} onClick={() => handleProfileClick('settings')}>
                                <div className="profile-item">Settings</div>
                            </Link>
                        </li>
                    </ul>
                </li>
            );
        } else {
            return (
                <li className="nav-item" key={path} id={text} {...(screenType === 'small-screen' ? { 'data-bs-dismiss': 'offcanvas' } : {})}>
                    <Link to={path} className='nav-link active' onClick={() => handleLinkClick(path)}>
                        { (path !== '/register') && (
                            <div className={`item ${navPath === path ? 'bold-text' : ''}`}>{text}</div>
                        )}
                        { (path === '/register') && (
                            <div className={`item ${navPath === path ? 'change-color-signup-button border-text' : 'items__signup-button'}`}>Sign up</div>
                        )}
                    </Link>
                </li>
            );
        }
    };

    const navigationLinks = (inputClassName, screenType) => {
        return (
            <ul className={inputClassName}>
                {isAuth && (
                    <React.Fragment>
                        {menuItems.map((item) => {
                            if (checkInStatusQuery.data) {
                                if (item.path !== '/checkin') {
                                    return navigationLink(item.path, item.text, screenType);
                                }
                                return null;
                            } else {
                                return navigationLink(item.path, item.text, screenType);
                            }
                        })}
                        <li className="nav-item">
                            <div className='nav-link active logout container'>
                                <button className='items__logout-button' onClick={handleLogout}>
                                    Logout
                                </button>
                            </div>
                        </li>
                    </React.Fragment>
                )}
                {!isAuth && (
                    <React.Fragment>
                        {authItems.map((item) => {
                            return navigationLink(item.path, item.text, screenType);
                        })}
                    </React.Fragment>
                )}
            </ul>
        );
    };

    useMemo(() => {
        if (checkInStatusQuery.data !== null) {
            return null;
        }
    }, [checkInStatusQuery.isFetched, checkInStatusQuery.data]);

    return (
        <nav ref={navbarRef} className="navbar navbar-expand-lg bg-white">
            <div className="container-fluid">
                <Link to={isAuth ? '/dashboard' : '/'} className='nav-link active' onClick={() => handleLinkClick(isAuth ? '/dashboard' : '/')}>
                    <div className='navbar-brand header__title mb-0 h1'>Wellness Buddy</div>
                </Link>
                <button className="navbar-toggler" type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvasNavbar" aria-controls="offcanvasNavbar" aria-label="Toggle navigation">
                    <span className="navbar-toggler-icon"></span>
                </button>
                <div className="offcanvas offcanvas-end" tabIndex="-1" id="offcanvasNavbar" aria-labelledby="offcanvasNavbarLabel">
                    <div className="offcanvas-header">
                        <h5 className="offcanvas-title" id="offcanvasNavbarLabel">Wellness Buddy</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                    </div>
                    <div className="offcanvas-body">
                        {isAuth && (
                            <div className="d-block d-lg-none">
                                <UserProfile
                                    avatarName={avatarNameQuery.data}
                                    screenType={'small-screen'} />
                            </div>
                        )}
                        {navigationLinks('navbar-nav justify-content-end flex-grow-1 d-lg-none small-screen-text', 'small-screen')}
                    </div>
                </div>
                <div className="collapse navbar-collapse" id="navbarTogglerDemo02">
                    {navigationLinks('navbar-nav ms-auto mb-2 mb-lg-0 large-screen-text', 'large-screen')}
                </div>

            </div>
        </nav>
    );
};

export default Navbar;
